import { Button, NothingToShow } from 'shared-ui';
import { Typography } from '@mui/material';

import { useLocale } from 'hooks/useLocale/useLocale';
import { DodAlert } from 'api/dod/dod.v2.types';
import { AppMessages } from 'i18n/messages';
import { BarPanel } from '../components/barPanel/BarPanel';
import { KpiTile } from '../components/kpiTile/KpiTile';
import { PanelSkeleton } from '../components/panelSkeleton/PanelSkeleton';
import { SectionCard } from '../components/sectionCard/SectionCard';
import { StateChip } from '../components/stateChip/StateChip';
import { DodStateTone } from '../components/stateChip/StateChip.types';
import { TimeSeriesPanel } from '../components/timeSeriesPanel/TimeSeriesPanel';
import { TkdHeatmap } from '../components/tkdHeatmap/TkdHeatmap';

import * as Styled from './OverviewTab.styles';
import { OverviewChart, OverviewTabProps } from './OverviewTab.types';
import { formatDurationSeconds, dodOutboxStatusLabel } from './dodFormat';

/**
 * "Czy system dostarcza?" — the answer above the fold.
 *
 * Nothing here invents a number. A KPI the backend could not compute arrives as the localised
 * "unknown" string, a chart whose rollup provider is absent renders an explicit message instead of
 * a flat zero line, and an alert chip is green because the backend says `firing: false`.
 */
export const OverviewTab = ({
  kpis,
  isKpiLoading,
  isError,
  sessions,
  connectedCount,
  totalConnections,
  alerts,
  outbox,
  throughput,
  queueDepth,
  isPerTkd,
  onTogglePerTkd,
  deadLetter,
  deadLetterByReason,
  outboxChart,
  heatmapCells,
  isHeatmapLoading,
  heatmapTitle,
  onSessionClick,
  onHeatmapSelect,
}: OverviewTabProps): JSX.Element => {
  const { formatMessage } = useLocale();

  // The backend picks the sentence (alert.code) and the numbers (alert.params); the wording lives
  // in the message catalogue so operators read it in their language. An unknown or missing code
  // (older backend) falls back to the English summary rather than to an empty chip.
  const alertText = (alert: DodAlert): string => {
    const key = alert.code ? (`dod.overview.alert.${alert.code}` as keyof typeof AppMessages) : undefined;
    return key && key in AppMessages ? formatMessage({ id: AppMessages[key] }, alert.params) : alert.summary;
  };

  /** Green because the backend says `firing: false`; the severity only decides how loud a firing one is. */
  const alertTone = (alert: DodAlert): DodStateTone =>
    alert.firing ? (alert.severity === 'critical' ? 'bad' : 'warn') : 'ok';

  const unknownLabel = formatMessage({ id: AppMessages['dod.overview.unknown'] });

  const renderChart = (chart: OverviewChart, actions?: JSX.Element, height?: number): JSX.Element => {
    // A failed request is not "no rollups": the panel renders its own error state instead.
    if (chart.isUnavailable && !chart.error) {
      return (
        <SectionCard title={chart.title} actions={actions}>
          <NothingToShow text={formatMessage({ id: AppMessages['dod.overview.unavailable'] })} />
        </SectionCard>
      );
    }

    return (
      <TimeSeriesPanel
        title={chart.title}
        series={chart.series}
        seriesLabels={chart.seriesLabels}
        seriesColors={chart.seriesColors}
        area={chart.area}
        stacked={chart.stacked}
        isLoading={chart.isLoading}
        error={chart.error}
        threshold={chart.threshold}
        annotations={chart.annotations}
        actions={actions}
        height={height}
      />
    );
  };

  if (isError) {
    return (
      <SectionCard title={formatMessage({ id: AppMessages['dod.overview.title'] })}>
        <div role="alert">
          <NothingToShow text={formatMessage({ id: AppMessages['dod.overview.error'] })} />
        </div>
      </SectionCard>
    );
  }

  return (
    <>
      <Styled.KpiRow>
        {kpis.map((kpi) => (
          <KpiTile
            key={kpi.id}
            label={kpi.label}
            value={kpi.value}
            delta={kpi.delta}
            goodDirection={kpi.goodDirection}
            tone={kpi.tone}
            sparkline={kpi.sparkline}
            caption={kpi.caption}
            isLoading={isKpiLoading}
          />
        ))}
      </Styled.KpiRow>

      <Styled.StripRow>
        <SectionCard
          title={formatMessage(
            { id: AppMessages['dod.overview.sessions.title'] },
            { connected: connectedCount, total: totalConnections },
          )}
        >
          {sessions.length === 0 ? (
            <NothingToShow text={formatMessage({ id: AppMessages['dod.overview.sessions.empty'] })} />
          ) : (
            <Styled.ChipRow>
              {sessions.map((session) => (
                <StateChip
                  key={session.rcpId}
                  state={session.state}
                  label={session.rcpName ?? session.rcpId}
                  caption={session.meta}
                  onClick={onSessionClick}
                />
              ))}
            </Styled.ChipRow>
          )}
        </SectionCard>

        <SectionCard title={formatMessage({ id: AppMessages['dod.overview.alerts.title'] })}>
          {alerts.length === 0 ? (
            <NothingToShow text={formatMessage({ id: AppMessages['dod.overview.alerts.empty'] })} />
          ) : (
            <Styled.AlertList>
              {alerts.map((alert) => {
                const tone = alertTone(alert);

                // A badge carries one word; the sentence stays readable body text beside it.
                return (
                  <Styled.AlertRow key={alert.id} role="status" data-tone={tone} data-testid={`dod-alert-${alert.id}`}>
                    <StateChip
                      tone={tone}
                      text={formatMessage({ id: AppMessages[`dod.overview.alert.state.${tone}`] })}
                    />
                    <Typography variant="body2">{alertText(alert)}</Typography>
                  </Styled.AlertRow>
                );
              })}
            </Styled.AlertList>
          )}
        </SectionCard>
      </Styled.StripRow>

      <Styled.ChartGrid>
        {renderChart(throughput)}

        {renderChart(
          queueDepth,
          <Button size="small" onClick={onTogglePerTkd}>
            {formatMessage({
              id: AppMessages[
                isPerTkd ? 'dod.overview.chart.queueDepth.total' : 'dod.overview.chart.queueDepth.perTkd'
              ],
            })}
          </Button>,
        )}

        {renderChart(deadLetter)}

        {deadLetterByReason.isLoading ? (
          <PanelSkeleton height={240} />
        ) : deadLetterByReason.isUnavailable ? (
          <SectionCard title={deadLetterByReason.title}>
            <NothingToShow text={formatMessage({ id: AppMessages['dod.common.empty'] })} />
          </SectionCard>
        ) : (
          <BarPanel title={deadLetterByReason.title} data={deadLetterByReason.data} layout="vertical" />
        )}

        <SectionCard title={outboxChart.title}>
          {renderChart({ ...outboxChart, title: '' }, undefined, 180)}
          <Styled.OutboxLegend>
            {outbox.map((row) => (
              <li key={row.status}>
                <span>{dodOutboxStatusLabel(row.status, formatMessage)}</span>
                <span>
                  {row.orders}
                  {row.oldestAgeSeconds !== null && ` · ${formatDurationSeconds(row.oldestAgeSeconds, unknownLabel)}`}
                </span>
              </li>
            ))}
          </Styled.OutboxLegend>
        </SectionCard>
      </Styled.ChartGrid>

      <TkdHeatmap title={heatmapTitle} cells={heatmapCells} isLoading={isHeatmapLoading} onSelect={onHeatmapSelect} />
    </>
  );
};
